'use client';

import React from 'react';
import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';
import { Icon } from '../Icon';
import { theme } from '../../theme';
import { isInternalHref, scrollToAnchor } from '@/app/lib/common/Button/button.utils';
import { cn } from '@/app/lib/utils';

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

export type LinkTarget = '_self' | '_blank';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-heading transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 cursor-pointer disabled:pointer-events-none font-semibold rounded-sm text-base px-sm py-3 min-w-45 h-11',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-white hover:bg-primary-500 disabled:bg-secondary-100 disabled:text-secondary-300',
        secondary:
          'border bg-white text-primary border-primary hover:bg-accent disabled:text-secondary-200 disabled:border-secondary-300',
        tertiary:
          'bg-secondary-400 text-white hover:bg-secondary-300 disabled:bg-secondary-100 disabled:text-secondary-300',
        text: 'text-primary bg-none border-none ring-none hover:text-primary-500 focus:ring-2 focus:!ring-primary focus:ring-offset-0!',
        softDisabled: 'bg-secondary-100 text-secondary-200',
      },
      site: {
        porta: '',
        portaNZ: '',
        redRec: '',
      },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        site: 'redRec',
        class: 'hover:bg-primary-100',
      },
      {
        variant: 'tertiary',
        site: 'redRec',
        class: 'bg-accent text-primary hover:bg-accent-400',
      },
    ],
    defaultVariants: { variant: 'primary', site: 'porta' },
  },
);

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: ButtonVariant;
  iconName?: React.ComponentProps<typeof Icon>['name'];
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  iconColor?: string;
  url?: string; // internal | external | #anchor
  openInNewTab?: boolean;
  isDisabled?: boolean;
  overrideClasses?: boolean;
}

export function Button({
  children,
  className,
  variant,
  iconName,
  iconPosition,
  iconSize = 24,
  iconColor,
  url,
  openInNewTab,
  isDisabled,
  onClick,
  overrideClasses,
  ...rest
}: ButtonProps) {
  const content = (
    <>
      {iconName && iconPosition === 'left' && <Icon name={iconName} size={iconSize} color={iconColor} />}
      {children}
      {iconName && iconPosition === 'right' && <Icon name={iconName} size={iconSize} color={iconColor} />}
    </>
  );

  const cls = cn(overrideClasses ? '' : buttonVariants({ variant, site: theme }), className);

  if (url && !isDisabled) {
    if (url.startsWith('#')) {
      return (
        <button
          type="button"
          className={cls}
          onClick={() => scrollToAnchor(url.slice(1))}
          aria-disabled={undefined}
          {...rest}
        >
          {content}
        </button>
      );
    }
    if (isInternalHref(url)) {
      return (
        <Link
          href={url}
          onClick={() => {
            // Not optimal, but ensures scroll to top after navigation
            !openInNewTab &&
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 100);
          }}
          className={cls}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
        >
          {content}
        </Link>
      );
    }
    return (
      <a
        href={url}
        className={cls}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={cls}
      onClick={!isDisabled ? onClick : undefined}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      {...rest}
    >
      {content}
    </button>
  );
}
